/**
 * "How many people is this?" — answered inside section 2, before a draft
 * exists (#1920).
 *
 * ── WHY IT IS A BUTTON AND NOT A LIVE COUNT ─────────────────────────────────
 * The query behind it is a range scan over `usage_rated_events`, the money
 * table. Recomputing on every checkbox tick would put dozens of those behind a
 * few seconds of an operator making up their mind, so the request is an
 * explicit act with a visible cost, the way `Recompute` in section 3 is.
 *
 * ── WHY IT IS NOT THE FREEZE, AND MUST NOT BECOME IT ────────────────────────
 * This shows a count and a sample of addresses so the operator can sanity-check
 * a selection — four people or four hundred — and nothing here writes a row.
 * The frozen list is still computed server-side at freeze time from the STORED
 * audience, and is still rendered in full before any send. Growing this panel
 * into "the list you approved" would quietly move that guarantee onto a surface
 * computed at a different instant from the one that gets mailed.
 */
import type { EmailNoticeRecipientPreviewResponse } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { Button } from '../../ui/index.ts';

/** How many addresses the sample lists before collapsing into a count. */
const SAMPLE_LIMIT = 24;

export interface AudiencePreviewControls {
  /** The draft audience is complete enough to be asked about. */
  readonly ready: boolean;
  readonly running: boolean;
  readonly result: EmailNoticeRecipientPreviewResponse | null;
  readonly onPreview: () => void;
}

export function AudiencePreviewPanel({
  preview,
}: {
  readonly preview: AudiencePreviewControls;
}): JSX.Element {
  const t = useT();
  const { ready, running, result, onPreview } = preview;

  return (
    <div
      className="flex flex-col gap-2 rounded border border-line-soft bg-well px-3 py-2.5"
      data-testid="email-notice-audience-preview"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          loading={running}
          disabled={!ready}
          onClick={onPreview}
          data-testid="email-notice-audience-preview-run"
        >
          {t('email_notices.audience.preview_action')}
        </Button>
        <span className="text-micro text-paper-low">
          {ready
            ? t('email_notices.audience.preview_hint')
            : t('email_notices.audience.preview_incomplete')}
        </span>
      </div>
      {result === null ? null : <PreviewResult result={result} />}
    </div>
  );
}

function PreviewResult({
  result,
}: {
  readonly result: EmailNoticeRecipientPreviewResponse;
}): JSX.Element {
  const t = useT();
  const sample = result.candidates.slice(0, SAMPLE_LIMIT);
  const hidden = result.candidates.length - sample.length;

  return (
    <>
      <p className="m-0 text-label text-paper-mid" data-testid="email-notice-audience-preview-count">
        {result.candidates.length === 0
          ? t('email_notices.audience.preview_none', {
              days: String(result.window_days),
              from: result.window_from,
            })
          : t('email_notices.audience.preview_count', {
              count: String(result.candidates.length),
              days: String(result.window_days),
              from: result.window_from,
            })}
      </p>
      {sample.length === 0 ? null : (
        <ul
          className="m-0 flex max-h-40 list-none flex-col gap-0.5 overflow-y-auto p-0"
          data-testid="email-notice-audience-preview-sample"
        >
          {sample.map((row) => (
            <li key={row.account_id} className="font-mono text-micro text-paper-low">
              {row.email}
            </li>
          ))}
        </ul>
      )}
      {hidden <= 0 ? null : (
        <p className="m-0 text-micro text-paper-faint">
          {t('email_notices.audience.preview_more', { count: String(hidden) })}
        </p>
      )}
      <p className="m-0 text-micro text-paper-faint">
        {t('email_notices.audience.preview_not_frozen')}
      </p>
    </>
  );
}
